import { faGavel } from '@fortawesome/pro-regular-svg-icons/faGavel';
import { faMoneyBill } from '@fortawesome/pro-regular-svg-icons/faMoneyBill';
import { KEY_INFO_TOOLTIPS } from '@propertypal/shared/src/constants/keyInfo';
import useInView from '@propertypal/shared/src/hooks/useInView';
import { MortgageCalcData } from '@propertypal/shared/src/hooks/useMortgageCalculator';
import CoOwnershipLogo from '@propertypal/shared/src/resources/logos/co-ownership-logo.svg?url';
import FibrusLogo from '@propertypal/shared/src/resources/logos/fibrus.svg';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { getItem } from '@propertypal/shared/src/services/local.storage';
import { BooleanKey } from '@propertypal/shared/src/types/legal-fees';
import { Property, KeyInfo as KeyInfoType } from '@propertypal/shared/src/types/property';
import boolCheck from '@propertypal/shared/src/utils/bool-check';
import floatToCurrency, { parseNumber } from '@propertypal/shared/src/utils/floatToCurrency';
import showBroadbandChecker from '@propertypal/shared/src/utils/property/broadband';
import {
  getDefaultDeposit,
  getDefaultTerm,
} from '@propertypal/shared/src/utils/property/getMortgageCalculatorDefaults';
import {
  groupAuctionDetails,
  groupFinancialInfo,
  groupPropertyInformation,
  ICON_MAP,
  STAMP_DUTY_TEXT_MAP,
} from '@propertypal/shared/src/utils/property/groupKeyInfo';
import React, { FunctionComponent, useEffect, useRef } from 'react';
import Loader from 'react-spinners/ClipLoader';
import { useTheme } from 'styled-components';
import { InfoBox, InfoRow } from '../layout/InfoBox';
import { Container, SponsorLogoBox, BottomRow, CoownershipText, LegalLogo } from './KeyInfo.style';

interface Props {
  buyingStatus: string | null;
  ukResident: '' | BooleanKey | null;
  property: Property;
  onClick: (info: KeyInfoType) => void;
  mortgageCalculator: MortgageCalcData;
  showLegalFees?: boolean;
}

const MORTGAGE_INFO: KeyInfoType = {
  key: 'TYPICAL_MORTGAGE',
  name: 'Mortgage',
  text: '',
  textHtml: '',
  group: '',
};

const LEGAL_ITEM = {
  key: 'LEGAL_CALCULATOR',
  name: 'Legal Calculator',
  text: '',
  textHtml: '',
  group: '',
};

const BROADBAND_ITEM = {
  key: 'BROADBAND',
  name: 'Broadband',
  text: '',
  textHtml: '',
  group: '',
};

export const renderItem = (
  property: Property,
  info: KeyInfoType,
  buyingStatus: string | null,
  onClick: Props['onClick'],
  ukResident?: Props['ukResident'],
  fibrusRef?: React.RefObject<HTMLButtonElement | null>,
) => {
  const tooltip = KEY_INFO_TOOLTIPS[info.key];

  if (info.key === 'STAMP_DUTY_COMPLEX' && info.buyerTypeCosts) {
    if (buyingStatus) {
      return (
        <InfoRow key={info.name} icon={ICON_MAP[info.key]} title={info.name} tooltip={tooltip?.content}>
          <button onClick={() => onClick(info)} type="button">
            {info.buyerTypeCosts[`${buyingStatus}${!ukResident || ukResident === 'FALSE' ? '_NON_UK' : ''}`]}*²
          </button>
          <br />
          <span style={{ display: 'inline-block', fontSize: 14 }}>
            Based on being a {STAMP_DUTY_TEXT_MAP[buyingStatus]}
            {' - '}
            <button
              type="button"
              style={{ display: 'inline', fontSize: 14 }}
              onClick={() => onClick(info)}
              data-testid="stamp-duty-button"
            >
              Change
            </button>
          </span>
        </InfoRow>
      );
    }

    return (
      <InfoRow key={info.name} icon={ICON_MAP[info.key]} title={info.name} tooltip={tooltip?.content}>
        <button type="button" onClick={() => onClick(info)} data-testid="stamp-duty-button">
          Calculate Stamp Duty
        </button>
      </InfoRow>
    );
  }

  if (info.key === 'STAMP_DUTY_SIMPLE') {
    return (
      <InfoRow key={info.name} icon={ICON_MAP[info.key]} title={info.name} tooltip={tooltip?.content}>
        <p>{info.text}*2</p>
      </InfoRow>
    );
  }

  if (info.key === 'BROADBAND') {
    return (
      <React.Fragment key={info.name}>
        <InfoRow icon={ICON_MAP[info.key]} title="Broadband Speed Checker">
          <button onClick={() => onClick(info)} type="button">
            {property?.broadbandSpeed?.fibrusAvailable
              ? 'Fibrus Available'
              : property.broadbandSpeed?.maxUfbbPredictedDown
              ? 'Ultrafast'
              : property.broadbandSpeed?.maxSfbbPredictedDown
              ? 'Superfast'
              : 'Standard'}
            {!property?.broadbandSpeed?.fibrusAvailable && ' Broadband'}
          </button>
        </InfoRow>

        <SponsorLogoBox ref={fibrusRef} onClick={() => onClick(info)} type="button">
          <FibrusLogo style={{ maxWidth: 80 }} />
          <span>
            In partnership
            <br />
            with Fibrus
          </span>
        </SponsorLogoBox>
      </React.Fragment>
    );
  }

  return (
    <InfoRow key={info.name} icon={ICON_MAP[info.key]} title={info.name} tooltip={tooltip?.content}>
      <p>{info.text}</p>
    </InfoRow>
  );
};

const KeyInfo: FunctionComponent<Props> = (props) => {
  const details = groupPropertyInformation(props.property.keyInfo);
  const broadbandChecker = showBroadbandChecker(props.property);
  const finances = groupFinancialInfo(props.property.keyInfo);
  const auctions = groupAuctionDetails(props.property.keyInfo);
  const { data, error, errorStatus, depositPercentage, hasMortgageCalculator, getMortgageResult } =
    props.mortgageCalculator;
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const fibrusRef = useRef<HTMLButtonElement>(null);
  const coOwnership = props.property.keyInfo?.find((info) => info.key === 'COOWNERSHIP');
  const coOwnershipRef = useRef<HTMLParagraphElement>(null);
  const mmwRef = useRef<HTMLButtonElement>(null);

  const fetchMortgageResult = () => {
    if (!data && !error && hasMortgageCalculator) {
      const defaultTerm = getItem<string>('defaultTerm');
      const defaultDeposit = getItem<string>('defaultDeposit');

      getMortgageResult({
        propertyValue: props.property.price?.price,
        paymentTerm: defaultTerm ? parseNumber(defaultTerm) : getDefaultTerm(props.property),
        deposit: defaultDeposit ? parseNumber(defaultDeposit) : getDefaultDeposit(props.property),
      });
    }
  };

  useInView(containerRef, { threshold: 0.5, rootMargin: '0px 0px 600px' }, fetchMortgageResult);

  useInView(fibrusRef, { threshold: 0.5 }, () => {
    trackGaEvent('key_info_fibrus_impression', {
      page: props.property?.development ? 'DDP' : 'PDP',
      fibrus: boolCheck(props.property?.broadbandSpeed?.fibrusAvailable),
    });
  });

  useInView(coOwnershipRef, { threshold: 0.5 }, () => {
    trackGaEvent('coownership_banner_impression', {
      page: 'PDP',
      location: props.property?.coOwnershipOnly ? 'LDP' : 'Property Details Page',
    });
  });

  useInView(mmwRef, { threshold: 0.5 }, () => {
    trackGaEvent('legal_fees_ad_impression', {
      page: 'PDP',
      location: 'Key Info',
    });
  });

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      // Anchor tag was clicked inside co-ownership banner
      if ((e?.target as HTMLElement)?.tagName === 'A') {
        trackGaEvent('coownership_banner_click', {
          page: 'PDP',
          location: props.property?.coOwnershipOnly ? 'LDP' : 'Property Details Page',
        });
      }
    };

    coOwnershipRef.current?.addEventListener('click', handleClick);

    return () => {
      coOwnershipRef.current?.removeEventListener('click', handleClick);
    };
  }, []);

  return (
    <>
      {!!auctions?.length && (
        <InfoBox title="Auction Details" containerStyle={{ margin: '30px 0' }}>
          {auctions.map((info) =>
            renderItem(props.property, info, props.buyingStatus, props.onClick, props.ukResident),
          )}
        </InfoBox>
      )}

      {(!!details?.length || broadbandChecker || !!finances?.length) && (
        <Container ref={containerRef}>
          {(!!details?.length || broadbandChecker) && (
            <InfoBox title="Property Information">
              {details?.map((info) =>
                renderItem(props.property, info, props.buyingStatus, props.onClick, props.ukResident, fibrusRef),
              )}

              {broadbandChecker && (
                <>
                  <InfoRow title="Broadband Speed Checker" icon={ICON_MAP.BROADBAND}>
                    <button onClick={() => props.onClick(BROADBAND_ITEM)} type="button">
                      Check Postcode
                    </button>
                  </InfoRow>

                  <SponsorLogoBox ref={fibrusRef} onClick={() => props.onClick(BROADBAND_ITEM)} type="button">
                    <FibrusLogo style={{ maxWidth: 80 }} />
                    <span>In partnership with Fibrus</span>
                  </SponsorLogoBox>
                </>
              )}
            </InfoBox>
          )}

          {!!finances?.length && (
            <InfoBox title="Financial Information">
              {finances.map((info) =>
                renderItem(props.property, info, props.buyingStatus, props.onClick, props.ukResident),
              )}

              {hasMortgageCalculator && (
                <InfoRow title="Mortgage Calculator" icon={faMoneyBill}>
                  {!data && !errorStatus && !error && <Loader color={theme.backgroundLight} size={28} />}

                  {!!errorStatus && (
                    <>
                      <p>No mortgage results</p>

                      <p style={{ fontSize: 12 }}>
                        Try changing your{' '}
                        <button style={{ fontSize: 12 }} onClick={() => props.onClick(MORTGAGE_INFO)} type="button">
                          criteria
                        </button>{' '}
                        below
                      </p>
                    </>
                  )}

                  {!errorStatus && data?.product && (
                    <>
                      <button onClick={() => props.onClick(MORTGAGE_INFO)} type="button">{`${floatToCurrency(
                        data.product.initialMonthlyPayment,
                      )} per month`}</button>

                      <p style={{ fontSize: 14 }}>
                        {floatToCurrency(data.deposit, {
                          precision: 0,
                        })}{' '}
                        {depositPercentage}% deposit
                        <br />({data.term} year term)
                      </p>
                    </>
                  )}
                </InfoRow>
              )}

              {props.showLegalFees && (
                <>
                  <InfoRow title="Legal Calculator" icon={faGavel}>
                    <button type="button" onClick={() => props.onClick(LEGAL_ITEM)} data-testid="legal-fees-btn">
                      Calculate Legal Costs
                    </button>
                  </InfoRow>
                  <SponsorLogoBox ref={mmwRef} type="button" onClick={() => props.onClick(LEGAL_ITEM)}>
                    <LegalLogo />
                    <span>In partnership with Millar McCall Wylie</span>
                  </SponsorLogoBox>
                </>
              )}
            </InfoBox>
          )}
        </Container>
      )}

      {coOwnership && (
        <BottomRow data-testid="co-ownership-banner">
          <img src={CoOwnershipLogo.src} className="co-ownership-logo" alt="Co-Ownership" />
          <CoownershipText ref={coOwnershipRef}>
            This property may be suitable for Co-Ownership. Find out how they could{' '}
            <a href="https://bit.ly/46fsa1q" target="_blank" rel="noreferrer">
              help you
            </a>{' '}
            become a homeowner.
          </CoownershipText>
        </BottomRow>
      )}
    </>
  );
};

export default KeyInfo;
